import {priority} from '@priority-software/priority-proto-api/lib';
import {ChartConfig, ReportInputMetaData} from '../../types';
import {
    ChartDimensions,
    MultiSeriesChartTypesOptions,
    SingleSeriesChartTypesOptions
} from '../../chartConsts';
import {IsCompany} from './../../consts';

export const getDataplotInputs = (
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions
) => {
    const dataplotInputs: ChartDimensions[] = [];
    switch (type) {
        // Multi-Series
        case MultiSeriesChartTypesOptions.msline:
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            // Dimensions order is important here. DO NOT CHANGE!!
            dataplotInputs.push(ChartDimensions.xAxis, ChartDimensions.series);
            break;
        // Basic Single-Series
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
        case SingleSeriesChartTypesOptions.line:
        case SingleSeriesChartTypesOptions.pie2d:
        case SingleSeriesChartTypesOptions.doughnut2d:
        case SingleSeriesChartTypesOptions.pyramid:
        case SingleSeriesChartTypesOptions.funnel:
            dataplotInputs.push(ChartDimensions.xAxis);
            break;
        // Bubble Chart
        case SingleSeriesChartTypesOptions.bubble:
            dataplotInputs.push(ChartDimensions.category);
            break;
        // Gauge and KPI are handled here
        default:
            break;
    }
    return dataplotInputs;
};

const getInputData = (dataplotInputs: ChartDimensions[], chartInfo: ChartConfig) => {
    return dataplotInputs.map(dataplot => {
        if (chartInfo[dataplot]) {
            return chartInfo[dataplot].IsCompany
                ? {nodeId: IsCompany, dataplotDimension: dataplot}
                : {
                      nodeId: chartInfo[dataplot].NodeId,
                      type: chartInfo[dataplot].Type,
                      dateFormat: chartInfo[dataplot].DateFormat,
                      dataplotDimension: dataplot,
                      isGrouped: Number(chartInfo[dataplot].Group) > 0
                  };
        } else {
            const category = chartInfo?.bubbleConfig?.category;
            if (category) {
                return category.IsCompany
                    ? {nodeId: IsCompany, dataplotDimension: dataplot}
                    : {
                          nodeId: category.NodeId,
                          type: category.Type,
                          dateFormat: category.DateFormat,
                          dataplotDimension: dataplot,
                          isGrouped: Number(category.Group) > 0
                      };
            } else {
                return null;
            }
        }
    });
};

const getIsDataplot = (input: priority.netitems.nrest.IReportInput, dataplotsNodeIds: string[]) => {
    return input[IsCompany]
        ? dataplotsNodeIds.includes(IsCompany)
        : dataplotsNodeIds.includes(input.Colname);
};

export const prepareInputsData = (chartInfo: ChartConfig): ReportInputMetaData[] => {
    const dataplotInputs = getDataplotInputs(chartInfo.type);
    const dataplotsData = getInputData(dataplotInputs, chartInfo);
    const dataplotsNodeIds = dataplotsData.map(dataplot => dataplot.nodeId);
    const chartInputs = chartInfo.explanatoryReport?.chartInputs;
    const res = chartInfo.explanatoryReport?.Inputs?.map(input => {
        const isDataplot = getIsDataplot(input, dataplotsNodeIds);
        let dateProps = null;
        let dataplotDimension = null;
        if (isDataplot) {
            const dataplot = dataplotsData.find(
                dataplotData =>
                    dataplotData.nodeId === input.Colname || dataplotData.nodeId === IsCompany
            );
            dateProps = {
                type: dataplot.type,
                dateFormat: dataplot.dateFormat,
                isGrouped: dataplot.isGrouped
            };
            dataplotDimension = dataplot.dataplotDimension;
        }
        return {
            isDataplot,
            dateProps,
            dataplotDimension,
            inputIndex: chartInputs.findIndex(
                chartInput =>
                    chartInput.NodeId === input.Colname ||
                    (chartInput.NodeId === '0:' && input[IsCompany])
            ),
            ...input
        };
    });
    return res;
};
